import React from 'react';
import { Popover, Button, Form } from 'react-bootstrap';
import { Trash2 } from 'lucide-react';
import moment from 'moment';

const TaskReminderPopover = ({ task, onAddReminder, onDeleteReminder, ...props }) => {
    const handleQuickReminder = (type) => {
        let date;
        const now = moment();
        const dueDate = task.due_date ? moment(task.due_date) : null;

        switch (type) {
            case 'later':
                // +3 hours, rounded to next hour
                date = now.add(3, 'hours').startOf('hour');
                // Avoid sleeping hours? If late night, maybe tomorrow morning?
                if (date.hour() > 21) date = now.add(1, 'days').hour(9).minute(0);
                break;
            case 'tomorrow':
                date = now.add(1, 'days').hour(9).minute(0);
                break;
            case 'weekend':
                date = now.day(6).hour(10).minute(0); // Next Saturday
                if (date.isBefore(now)) date = date.add(1, 'weeks');
                break;
            case 'next-week':
                date = now.add(1, 'weeks').startOf('isoWeek').hour(9).minute(0); // Next Monday
                break;
            // Relative to Due Date
            case 'due-1-day':
                if (dueDate) date = dueDate.subtract(1, 'days').hour(9).minute(0);
                break;
            case 'due-2-days':
                if (dueDate) date = dueDate.subtract(2, 'days').hour(9).minute(0);
                break;
            case 'due-1-hour':
                if (dueDate) date = dueDate.subtract(1, 'hours');
                break;
            default:
                return;
        }

        if (date && date.isValid()) {
            // Clean existing
            if (task.reminders && task.reminders.length > 0) {
                task.reminders.forEach(r => onDeleteReminder(task.id, r.id));
            }
            onAddReminder(task.id, date.toISOString());
            document.body.click(); // Close
        }
    };

    const [customDate, setCustomDate] = React.useState(
        task.reminders && task.reminders[0] ? moment(task.reminders[0].remind_at).format('YYYY-MM-DDTHH:mm') : ''
    );

    const handleCustomSave = () => {
        if (!customDate) return;

        // Clean existing
        if (task.reminders && task.reminders.length > 0) {
            task.reminders.forEach(r => onDeleteReminder(task.id, r.id));
        }

        const date = new Date(customDate);
        onAddReminder(task.id, date.toISOString());
        document.body.click(); // Close popover
    };

    return (
        <Popover id={`popover-reminder-${task.id}`} {...props}>
            <Popover.Header as="h3">Set Reminder</Popover.Header>
            <Popover.Body>
                <div className="d-flex flex-column gap-2" style={{ minWidth: '250px' }}>
                    <div className="d-grid gap-1">
                        <Button variant="light" size="sm" className="text-start" onClick={() => handleQuickReminder('later')}>
                            Later Today <span className="text-muted small float-end">{moment().add(3, 'hours').startOf('hour').format('HH:mm')}</span>
                        </Button>
                        <Button variant="light" size="sm" className="text-start" onClick={() => handleQuickReminder('tomorrow')}>
                            Tomorrow <span className="text-muted small float-end">{moment().add(1, 'days').hour(9).minute(0).format('ddd HH:mm')}</span>
                        </Button>
                        <Button variant="light" size="sm" className="text-start" onClick={() => handleQuickReminder('weekend')}>
                            This Weekend <span className="text-muted small float-end">Sat 10:00</span>
                        </Button>
                        <Button variant="light" size="sm" className="text-start" onClick={() => handleQuickReminder('next-week')}>
                            Next Week <span className="text-muted small float-end">{moment().add(1, 'weeks').startOf('isoWeek').format('ddd D.')}</span>
                        </Button>
                    </div>

                    {task.due_date && (
                        <>
                            <hr className="my-1" />
                            <div className="text-muted small mb-1">Relative to Due Date</div>
                            <div className="d-grid gap-1">
                                <Button variant="light" size="sm" className="text-start" onClick={() => handleQuickReminder('due-1-day')}>
                                    1 Day Before <span className="text-muted small float-end">9:00</span>
                                </Button>
                                <Button variant="light" size="sm" className="text-start" onClick={() => handleQuickReminder('due-2-days')}>
                                    2 Days Before <span className="text-muted small float-end">9:00</span>
                                </Button>
                                <Button variant="light" size="sm" className="text-start" onClick={() => handleQuickReminder('due-1-hour')}>
                                    1 Hour Before
                                </Button>
                            </div>
                        </>
                    )}

                    <hr className="my-1" />

                    <div className="text-muted small mb-1">Custom Date & Time</div>
                    <div className="d-flex gap-2">
                        <Form.Control
                            type="datetime-local"
                            size="sm"
                            value={customDate}
                            onChange={(e) => setCustomDate(e.target.value)}
                        />
                        <Button variant="primary" size="sm" onClick={handleCustomSave} disabled={!customDate}>
                            Set
                        </Button>
                    </div>
                    {task.reminders && task.reminders.length > 0 && (
                        <div className="d-flex flex-column gap-1 mt-2">
                            <small className="text-muted fw-bold">Active Reminder</small>
                            {task.reminders.map(r => (
                                <div key={r.id} className="d-flex justify-content-between align-items-center bg-light p-1 rounded">
                                    <small>{moment(r.remind_at).format('MMM D, HH:mm')}</small>
                                    <Trash2
                                        size={12}
                                        className="text-danger cursor-pointer"
                                        onClick={() => onDeleteReminder(task.id, r.id)}
                                        style={{ cursor: 'pointer' }}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </Popover.Body>
        </Popover>
    );
};

export default TaskReminderPopover;
